import { FC } from "react";
import { FieldArray, useFormikContext } from "formik";
import { FieldGroup } from "@blanket/components/layout/FieldGroup";
import Button from "@blanket/components/buttons/Button";
import TooltippedRemoveButton from "@blanket/components/buttons/TooltippedRemoveButton";
import {
  TextFormField,
  YesNoFormField,
} from "@blanket/components/formFields/FormFields";
import SubGroupPanel from "@blanket/components/formSections/SubGroupPanel";
import type { CompanyRegistrationValues } from "../../model/registrationTypes";
import { JaNej } from "../../model/registrationEnums";
import { emptyEmployee } from "../../model/registrationInitialValues";
import type { EmployeePhoneFieldConfig } from "./employeePhoneFieldConfig";

type EmployeesFieldArrayProps = {
  namePrefix: "enkeltmandsvirksomhed" | "interessentskab" | "advokatselskab";
  phoneField: EmployeePhoneFieldConfig;
  employeesQuestionLabel?: string;
};

const getEmployeeBlock = (
  values: CompanyRegistrationValues,
  namePrefix: EmployeesFieldArrayProps["namePrefix"],
) => {
  if (namePrefix === "enkeltmandsvirksomhed") {
    return values.enkeltmandsvirksomhed;
  }

  if (namePrefix === "interessentskab") {
    return values.interessentskab;
  }

  return values.advokatselskab;
};

export const EmployeesFieldArray: FC<EmployeesFieldArrayProps> = ({
  namePrefix,
  phoneField,
  employeesQuestionLabel = "Er der ansatte i virksomheden?",
}) => {
  const { values } = useFormikContext<CompanyRegistrationValues>();
  const block = getEmployeeBlock(values, namePrefix);

  return (
    <>
      <YesNoFormField
        name={`${namePrefix}.employees`}
        label={employeesQuestionLabel}
        isRequired
        helperText="Ansatte tæller kun advokater og fuldmægtige. Alle skal angives her."
        id={`input-${namePrefix}-employees`}
      />
      {block.employees === JaNej.Ja && (
        <SubGroupPanel
          className="blanket-participant-subsection"
          title="Angiv ansatte"
        >
          <FieldArray name={`${namePrefix}.employeeList`}>
            {({ push, remove }) => (
              <FieldGroup className="blanket-participant-list">
                {block.employeeList.map((employee, index) => (
                  <div
                    key={employee._rowId}
                    className="blanket-participant-card"
                  >
                    <div className="blanket-two-col">
                      <TextFormField
                        name={`${namePrefix}.employeeList.${index}.employeeName`}
                        label="Navn"
                        id={`input-${namePrefix}-employee-name-${index}`}
                        isRequired
                      />
                      <TextFormField
                        name={`${namePrefix}.employeeList.${index}.employeeRole`}
                        label="Stilling"
                        id={`input-${namePrefix}-employee-role-${index}`}
                        isRequired
                      />
                      <TextFormField
                        name={`${namePrefix}.employeeList.${index}.employeeEmail`}
                        label="E-mailadresse"
                        id={`input-${namePrefix}-employee-email-${index}`}
                        type="email"
                        isRequired
                      />
                      <TextFormField
                        name={`${namePrefix}.employeeList.${index}.employeePhone`}
                        label={phoneField.label}
                        helperText={phoneField.helperText}
                        id={`input-${namePrefix}-employee-phone-${index}`}
                        type="tel"
                        isRequired={phoneField.isRequired}
                      />
                    </div>
                    <div className="mt-3 d-flex justify-content-end">
                      <TooltippedRemoveButton
                        onClick={() => remove(index)}
                        ariaLabel="Fjern ansat"
                        disabled={block.employeeList.length <= 1}
                        tooltipContent="Mindst én ansat skal angives"
                      >
                        Fjern
                      </TooltippedRemoveButton>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  fullWidth={false}
                  onClick={() => push(emptyEmployee())}
                  aria-label="Tilføj ansat"
                >
                  Tilføj ansat
                </Button>
              </FieldGroup>
            )}
          </FieldArray>
        </SubGroupPanel>
      )}
    </>
  );
};
